import { Link } from '@tanstack/react-router'
import { COMPLIANCE_LABELS, INDUSTRIES, getAddon, getPlan } from '@shared/catalog'
import { MarketingShell } from '../components/Layout'
import { Card, Chip } from '../ui'

export function Industries() {
  return (
    <MarketingShell>
      <section className="container section stack gap-4" style={{ paddingBlock: '56px 24px' }}>
        <span className="eyebrow">Industries</span>
        <h1 className="display-small" style={{ maxWidth: '20ch' }}>
          Sector defaults, not a blank workspace
        </h1>
        <p className="body-large text-muted" style={{ maxWidth: '62ch' }}>
          Every industry below ships with a plan, capacity, compliance modules, and workflow
          templates already chosen. You start from something that works and adjust from there.
        </p>
      </section>

      <section className="container" style={{ paddingBottom: '80px' }}>
        <div className="stack gap-5">
          {INDUSTRIES.map((industry) => {
            const plan = getPlan(industry.presets.plan)!
            return (
              <Card key={industry.id} variant="outlined">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(260px, 1fr)',
                    gap: '32px',
                  }}
                  className="industry-grid"
                >
                  <div className="stack gap-4">
                    <div className="stack gap-2">
                      <div className="row gap-2 wrap items-baseline">
                        <h2 className="headline-small">{industry.name}</h2>
                        {industry.compliance
                          .filter((c) => c !== 'none')
                          .map((c) => (
                            <Chip key={c} tone="warn">
                              {COMPLIANCE_LABELS[c]}
                            </Chip>
                          ))}
                      </div>
                      <p className="body-large text-muted">{industry.description}</p>
                    </div>

                    <div className="stack gap-2">
                      <h3 className="title-small">Why this default</h3>
                      <p className="body-medium text-muted">{industry.presetRationale}</p>
                    </div>

                    <div className="stack gap-2">
                      <h3 className="title-small">Workflow templates</h3>
                      <div className="row gap-2 wrap">
                        {industry.workflowTemplates.map((w) => (
                          <Chip key={w} tone="assist">
                            {w}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="stack gap-4">
                    <div
                      className="stack gap-3"
                      style={{
                        padding: '20px',
                        borderRadius: 'var(--md-shape-md)',
                        background: 'var(--md-surface-container-high)',
                      }}
                    >
                      <span className="eyebrow">Preset configuration</span>
                      <dl className="stack gap-2" style={{ margin: 0 }}>
                        <SpecRow label="Plan" value={plan.name} />
                        <SpecRow label="Seats" value={String(industry.presets.seats)} />
                        <SpecRow label="Numbers" value={String(industry.presets.numbers)} />
                        <SpecRow
                          label="Conversations"
                          value={`${industry.presets.conversations.toLocaleString()}/mo`}
                        />
                      </dl>
                      <hr className="divider" />
                      <div className="stack gap-2">
                        <span className="label-medium text-muted">Modules</span>
                        <div className="row gap-2 wrap">
                          {industry.presets.addons.map((a) => (
                            <Chip key={a} tone="assist">
                              {getAddon(a)?.name ?? a}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    </div>

                    <Link
                      to="/industries/$industryId"
                      params={{ industryId: industry.id }}
                      className="btn btn-outlined btn-full"
                    >
                      See the {industry.name} page
                    </Link>
                    <Link
                      to="/pricing"
                      search={{ industry: industry.id }}
                      className="btn btn-tonal btn-full"
                    >
                      Price this configuration
                    </Link>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </section>
    </MarketingShell>
  )
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row between gap-3">
      <dt className="body-medium text-muted">{label}</dt>
      <dd className="body-medium tabular" style={{ margin: 0, fontWeight: 600 }}>
        {value}
      </dd>
    </div>
  )
}
